/**
 * RoomCalendarCard — Wochenkalender pro Raum mit Buchungsanzeige.
 * Design analog zur Fahrzeugbuchung: Titel · Zeit · Bucher; eigene Buchung hervorgehoben.
 */
import React from 'react';
import { format, isSameDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { DoorOpen, MapPin, Users, Plus, X, Lock } from 'lucide-react';
import { toast } from 'sonner';

const todayStr = () => format(new Date(), 'yyyy-MM-dd');

const bookingTimeRange = (b) =>
  `${b.start_datetime.slice(11, 16)} – ${b.end_datetime.slice(11, 16)}`;

const getBookingsForDay = (bookings, roomId, date) => {
  const dayStr = format(date, 'yyyy-MM-dd');
  return bookings.filter(
    (b) => b.room_id === roomId && b.start_datetime.startsWith(dayStr)
  );
};

export const RoomCalendarCard = ({
  room,
  location,
  weekDays,
  bookings,
  onBook,
  currentUserId,
  canManage,
  onDelete,
}) => (
  <Card className="overflow-hidden" data-testid={`room-card-${room.id}`}>
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-white flex-shrink-0"
            style={{ backgroundColor: room.color || '#6366f1' }}
          >
            <DoorOpen className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <CardTitle className="text-lg truncate">{room.name}</CardTitle>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mt-1">
              {location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {location.name}
                </span>
              )}
              {room.capacity && (
                <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {room.capacity} Personen
                </span>
              )}
            </div>
          </div>
        </div>
        {room.equipment && room.equipment.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {room.equipment.slice(0, 3).map((eq) => (
              <Badge key={eq} variant="secondary" className="text-xs">{eq}</Badge>
            ))}
          </div>
        )}
      </div>
    </CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <div className="min-w-[700px]">
          {/* Header */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {weekDays.map((d) => {
              const isToday = format(d, 'yyyy-MM-dd') === todayStr();
              return (
                <div
                  key={d.toISOString()}
                  className={`text-center p-2 rounded text-xs font-medium ${
                    isToday ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
                  }`}
                >
                  <div>{format(d, 'EEE', { locale: de })}</div>
                  <div className="text-base font-semibold mt-0.5">
                    {format(d, 'd.M.')}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tageszellen */}
          <div className="grid grid-cols-7 gap-2 items-stretch">
            {weekDays.map((day) => {
              const dayBookings = getBookingsForDay(bookings, room.id, day);
              const isToday = isSameDay(day, new Date());
              const isPast = day < new Date() && !isToday;

              return (
                <div
                  key={day.toISOString()}
                  onClick={() => {
                    if (isPast) {
                      toast.info('Vergangene Tage können nicht gebucht werden.');
                      return;
                    }
                    onBook(room, day);
                  }}
                  data-testid={`cell-${room.id}-${format(day, 'yyyy-MM-dd')}`}
                  title={isPast ? 'Vergangenheit — nicht buchbar' : 'Klicken zum Buchen'}
                  className={`border rounded-lg p-1 min-h-[110px] flex flex-col gap-1 transition-colors ${
                    isPast
                      ? 'bg-muted/30 opacity-60 cursor-not-allowed'
                      : 'bg-card hover:bg-secondary/40 cursor-pointer'
                  } ${isToday ? 'border-primary' : ''}`}
                >
                  {dayBookings.length === 0 ? (
                    isPast ? (
                      <div className="flex items-center justify-center h-full text-muted-foreground/60">
                        <Lock className="h-3 w-3" />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full opacity-0 sm:hover:opacity-60 transition-opacity">
                        <Plus className="h-4 w-4" />
                      </div>
                    )
                  ) : (
                    dayBookings.map((b) => {
                      const isMine = b.created_by === currentUserId;
                      const canDelete = isMine || canManage;
                      return (
                        <div
                          key={b.id}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`booking-${b.id}`}
                          className={`text-[11px] p-1.5 rounded ${
                            isMine
                              ? 'bg-primary/15 border border-primary/40'
                              : 'bg-amber-100 dark:bg-amber-900/30 border border-amber-300/50'
                          }`}
                        >
                          <div className="font-medium truncate flex items-center justify-between gap-1">
                            <span className="truncate" title={b.title}>{b.title}</span>
                            {canDelete && (
                              <button
                                type="button"
                                onClick={() => onDelete && onDelete(b)}
                                className="hover:text-red-600 flex-shrink-0"
                                title="Buchung löschen"
                                data-testid={`btn-delete-booking-${b.id}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                          <div className="opacity-70 truncate">{bookingTimeRange(b)}</div>
                          {b.created_by_name && (
                            <div className="opacity-70 truncate" title={b.created_by_name}>
                              {b.created_by_name}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);
